import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  isSapCollateralChangeType,
  type SapCollateralEstate,
  type SapCollateralOther,
  type SapCollateralRecord,
  type SapCollateralSurety,
} from '@/utils/approval/sapCollateralChange';

type ContentProps = {
  apvType: number;
  before: SapCollateralRecord | null;
  after: SapCollateralRecord | null;
  t: TFunction;
};

type GroupField<T> = { key: keyof T; label: string };

const ESTATE_FIELDS: GroupField<SapCollateralEstate>[] = [
  { key: 'ESTATETYPE', label: '물건종류' },
  { key: 'ESTATEVALUE', label: '평가총액' },
  { key: 'SECURITYAMT', label: '담보설정액' },
  { key: 'SECURITYRANKING', label: '당사담보순위' },
];

const SURETY_FIELDS: GroupField<SapCollateralSurety>[] = [
  { key: 'SURETY_AMT', label: '보증보험액' },
  { key: 'SURETY_TODATE', label: '보험만료일' },
];

const OTHER_FIELDS: GroupField<SapCollateralOther>[] = [
  { key: 'OTHERSECURITY', label: '기타담보내용' },
  { key: 'OTHERSECURITYAMT', label: '기타담보금액' },
  { key: 'OTHERSECURITYDATE', label: '기타담보설정일자' },
  { key: 'FROMAVAILABLEDATE', label: '기타담보시작일' },
  { key: 'TOAVAILABLEDATE', label: '기타담보종료일' },
];

/** 열(154 신규 / 155 변경 전·후) 중 많은 쪽 건수만큼, 없는 쪽은 빈칸(레거시 동일) */
const GroupRows = <T,>({
  title,
  fields,
  values,
  t,
}: {
  title: string;
  fields: GroupField<T>[];
  values: T[][];
  t: TFunction;
}) => (
  <>
    {Array.from({ length: Math.max(...values.map((v) => v.length)) }, (_, i) =>
      fields.map((field, j) => (
        <tr key={`${title}-${i}-${String(field.key)}`}>
          {j === 0 && (
            <td rowSpan={fields.length}>
              {t(title)} {i + 1}
            </td>
          )}
          <td>{t(field.label)}</td>
          {values.map((column, k) => (
            <td key={k}>{String(column[i]?.[field.key] ?? '')}</td>
          ))}
        </tr>
      )),
    )}
  </>
);

const listOf = <T,>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

const SapCollateralCreationContent = ({
  record,
  t,
}: {
  record: SapCollateralRecord | null;
  t: TFunction;
}) => {
  const head = record?.HEAD;
  const head2 = record?.HEAD2;
  const rows: [string, string | undefined][] = [
    ['브랜드', head?.KATR2],
    ['매장코드', head?.ALTKN],
    ['거래처 단축명', head?.ALTKN_T],
    ['고객번호 SAP', head?.KUNNR],
    ['비고', head2?.COMMENTS],
    ['총 여신금액', head2?.CREDIT_AMT],
    ['담보 총액', head2?.TOT_COLL_AMT],
    ['사용여부', head2?.USEYN],
  ];
  return (
    <table id="shopLending_table">
      <colgroup>
        <col style={{ width: '22%' }} />
        <col style={{ width: '28%' }} />
        <col style={{ width: '50%' }} />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={2}>{t('항목')}</th>
          <th>{t('신규', { context: 'collateral' })}</th>
        </tr>
      </thead>
      <tbody>
        {record && (
          <>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td colSpan={2}>{t(label)}</td>
                <td>{value ?? ''}</td>
              </tr>
            ))}
            <GroupRows
              title="부동산"
              fields={ESTATE_FIELDS}
              values={[listOf(record.ESTATE)]}
              t={t}
            />
            <GroupRows
              title="보증보험"
              fields={SURETY_FIELDS}
              values={[listOf(record.SURETY)]}
              t={t}
            />
            <GroupRows
              title="기타담보"
              fields={OTHER_FIELDS}
              values={[listOf(record.OTHER)]}
              t={t}
            />
          </>
        )}
      </tbody>
    </table>
  );
};

const SapCollateralChangeContent = ({
  before,
  after,
  t,
}: Omit<ContentProps, 'apvType'>) => {
  const head = before?.HEAD;
  const basics: [string, string | undefined][] = [
    ['브랜드', head?.KATR2],
    ['매장코드', head?.ALTKN],
    ['거래처 단축명', head?.ALTKN_T],
    ['고객번호 SAP', head?.KUNNR],
  ];
  const usages: [string, keyof NonNullable<SapCollateralRecord['HEAD2']>][] = [
    ['비고', 'COMMENTS'],
    ['총 여신금액', 'CREDIT_AMT'],
    ['담보 총액', 'TOT_COLL_AMT'],
    ['사용여부', 'USEYN'],
  ];
  return (
    <table id="shopLending_table">
      <colgroup>
        <col style={{ width: '12%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '35%' }} />
        <col style={{ width: '35%' }} />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={2}>{t('항목')}</th>
          <th>{t('변경 전')}</th>
          <th>{t('변경 후')}</th>
        </tr>
      </thead>
      <tbody>
        {before && after && (
          <>
            {basics.map(([label, value]) => (
              <tr key={label}>
                <td colSpan={2}>{t(label)}</td>
                <td colSpan={2}>{value ?? ''}</td>
              </tr>
            ))}
            {usages.map(([label, key]) => (
              <tr key={label}>
                <td colSpan={2}>{t(label)}</td>
                <td>{before.HEAD2?.[key] ?? ''}</td>
                <td>{after.HEAD2?.[key] ?? ''}</td>
              </tr>
            ))}
            <GroupRows
              title="부동산"
              fields={ESTATE_FIELDS}
              values={[listOf(before.ESTATE), listOf(after.ESTATE)]}
              t={t}
            />
            <GroupRows
              title="보증보험"
              fields={SURETY_FIELDS}
              values={[listOf(before.SURETY), listOf(after.SURETY)]}
              t={t}
            />
            <GroupRows
              title="기타담보"
              fields={OTHER_FIELDS}
              values={[listOf(before.OTHER), listOf(after.OTHER)]}
              t={t}
            />
          </>
        )}
      </tbody>
    </table>
  );
};

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌. 조회 실패면 머리글만 있는 표(레거시 동일) */
export const buildSapCollateralChangeContentHtml = ({
  apvType,
  ...props
}: ContentProps): string =>
  renderToStaticMarkup(
    isSapCollateralChangeType(apvType) ? (
      <SapCollateralChangeContent {...props} />
    ) : (
      <SapCollateralCreationContent record={props.after} t={props.t} />
    ),
  );
